import {
  memo,
  useCallback,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'

import { QuantityCounterButtonStyled } from './styles/QuantityCounterButtonStyled'
import { QuantityCounterStyled } from './styles/QuantityCounterStyled'
import { QuantityValueInputStyled } from './styles/QuantityValueInputStyled'
import { QuantityValueStyled } from './styles/QuantityValueStyled'
import { Icon } from '@ui/elements/Icon'

const MIN_VALUE = 1

export type QuantityCounterChangeEvent =
  | MouseEvent<HTMLButtonElement>
  | KeyboardEvent<HTMLInputElement>
  | FocusEvent<HTMLInputElement>

export interface IQuantityCounterProps {
  /**
   * Current quantity value
   */
  value: number
  /**
   * Callback when quantity changes (increment/decrement)
   */
  onChange: (newValue: number, event: QuantityCounterChangeEvent) => void
  /**
   * Callback when delete action is triggered (when value is at minimum)
   */
  onDelete?: (event: MouseEvent<HTMLButtonElement>) => void
  /**
   * Maximum allowed value (optional)
   */
  max?: number
  /**
   * Disable all interactions
   */
  disabled?: boolean
  /**
   * Disables the remove button independently of `disabled`.
   * Defaults to `disabled` value if not provided.
   */
  isRemoveDisabled?: boolean
  /**
   * Lets the value be typed instead of only stepped. Off by default so the
   * counter keeps the read-only display everywhere it is already used.
   */
  isValueEditable?: boolean
  /**
   * ARIA label for the control
   */
  'aria-label'?: string
  valueInputAriaLabel?: string
  removeButtonAriaLabel?: string
  increaseButtonAriaLabel?: string
  decreaseButtonAriaLabel?: string
  className?: string
}

export const QuantityCounter = memo(
  ({
    value,
    onChange,
    onDelete,
    max,
    disabled = false,
    isRemoveDisabled: isRemoveDisabledProp,
    isValueEditable = false,
    'aria-label': ariaLabel,
    valueInputAriaLabel,
    removeButtonAriaLabel,
    increaseButtonAriaLabel,
    decreaseButtonAriaLabel,
    className,
  }: IQuantityCounterProps) => {
    const [draft, setDraft] = useState<Nullable<string>>(null)
    const clampToBounds = useCallback(
      (candidate: number) => {
        const atLeastMinimum = Math.max(candidate || MIN_VALUE, MIN_VALUE)

        return max !== undefined
          ? Math.min(atLeastMinimum, max)
          : atLeastMinimum
      },
      [max]
    )

    // A typed quantity is the one the person means, so the steppers and the
    // remove affordance read from it rather than from the last committed
    // value. An emptied field states no quantity at all - it is on the way to
    // one - so it keeps deferring to the committed value instead of reading as
    // a request to remove the item.
    const hasTypedQuantity = draft !== null && draft !== ''
    const currentValue = hasTypedQuantity ? clampToBounds(Number(draft)) : value
    const isAtOrBelowOne = currentValue <= 1
    const isAtMaximum = max !== undefined && currentValue >= max
    const showRemoveButton = isAtOrBelowOne && !!onDelete
    const displayValue = draft ?? value.toString()
    const numDigits = displayValue.length
    const isRemoveDisabled = isRemoveDisabledProp ?? disabled

    // Clicking a button must not blur the field first: the commit that a blur
    // triggers would change the value, and the remove and decrement buttons
    // share a slot, so the click could land on whichever one the re-render
    // put there.
    const handleButtonMouseDown = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => event.preventDefault(),
      []
    )

    const handleDecrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtOrBelowOne) {
          const newValue = currentValue - 1
          setDraft(null)
          onChange(newValue, event)
        }
      },
      [disabled, isAtOrBelowOne, currentValue, onChange]
    )

    const handleIncrement = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!disabled && !isAtMaximum) {
          const newValue = currentValue + 1
          setDraft(null)
          onChange(newValue, event)
        }
      },
      [disabled, isAtMaximum, currentValue, onChange]
    )

    const handleDraftChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) =>
        setDraft(event.target.value.replace(/\D/g, '')),
      []
    )

    const commitDraft = useCallback(
      (
        event: KeyboardEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>
      ) => {
        if (draft === null) return

        const committed = clampToBounds(Number(draft))

        setDraft(null)
        if (committed !== value) onChange(committed, event)
      },
      [draft, clampToBounds, onChange, value]
    )

    const handleInputKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        // Escape abandons the draft, so the field shows the quantity the
        // consumer still holds. The blur that follows finds no draft to commit.
        if (event.key === 'Escape') {
          setDraft(null)
          return
        }

        if (event.key !== 'Enter') return
        event.preventDefault()
        commitDraft(event)
      },
      [commitDraft]
    )

    const handleDelete = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (!isRemoveDisabled) {
          onDelete(event)
        }
      },
      [isRemoveDisabled, onDelete]
    )

    return (
      <QuantityCounterStyled
        className={className}
        role='group'
        aria-label={ariaLabel || 'Quantity counter'}
        $isValueEditable={isValueEditable}
      >
        {showRemoveButton ? (
          <QuantityCounterButtonStyled
            onClick={handleDelete}
            onMouseDown={handleButtonMouseDown}
            disabled={isRemoveDisabled}
            aria-label={removeButtonAriaLabel || 'Remove item'}
          >
            <Icon icon='delete' />
          </QuantityCounterButtonStyled>
        ) : (
          <QuantityCounterButtonStyled
            onClick={handleDecrement}
            onMouseDown={handleButtonMouseDown}
            disabled={disabled || isAtOrBelowOne}
            aria-label={decreaseButtonAriaLabel || 'Decrease quantity'}
          >
            <Icon icon='minus' />
          </QuantityCounterButtonStyled>
        )}
        {isValueEditable ? (
          <QuantityValueInputStyled
            type='text'
            inputMode='numeric'
            numDigits={numDigits}
            value={displayValue}
            placeholder={MIN_VALUE.toString()}
            disabled={disabled}
            aria-label={valueInputAriaLabel || 'Quantity'}
            onChange={handleDraftChange}
            onKeyDown={handleInputKeyDown}
            onBlur={commitDraft}
          />
        ) : (
          <QuantityValueStyled numDigits={numDigits}>
            {value}
          </QuantityValueStyled>
        )}
        <QuantityCounterButtonStyled
          onClick={handleIncrement}
          onMouseDown={handleButtonMouseDown}
          disabled={disabled || isAtMaximum}
          aria-label={increaseButtonAriaLabel || 'Increase quantity'}
        >
          <Icon icon='add' />
        </QuantityCounterButtonStyled>
      </QuantityCounterStyled>
    )
  }
)

QuantityCounter.displayName = 'QuantityCounter'
